import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaHome, FaChartLine, FaBriefcase, FaMoneyBillWave, FaFileContract, FaHistory, FaBoxOpen } from 'react-icons/fa';
import { Tile } from '../../../components/EntityList';
import { useLabels } from '../../../hooks/useLabels';

/**
 * KPI tiles from the Inicio handoff spec — Cuentas/Alquileres/Ventas del
 * mes/Activos fijos — plus two follow-up tiles restoring data that the
 * redesign otherwise dropped from the legacy Créditos/Deudas/Contracts
 * bottom cards (Créditos y Deudas summary, Contratos activos/cerrados).
 * Values are all pulled from Dashboard.jsx's existing derived aggregates,
 * never re-fetched or re-computed here.
 */
function KpiGrid({
	dashboardModules,
	loaded,
	accounts,
	newAccountsCurrentMonth,
	contracts,
	newContractsCurrentMonth,
	activeContracts,
	closedContracts,
	rentalPending,
	rentalOverdue,
	assetSales,
	newAssetSalesCurrentMonth,
	currentMonthSalesUSD,
	currentMonthSalesUYU,
	assets,
	newAssetsCurrentMonth,
	totalSalesUSD,
	totalSalesUYU,
	soldAssets,
	availableAssets,
	credits,
	debts,
	accountsWithDebts,
	accountsWithCredits,
	formatCurrency,
}) {
	const navigate = useNavigate();
	const { t } = useLabels();

	// Dash every number until the initial fetch resolves, rather than a
	// confident-looking 0 — and pin tones to neutral so the sub-line's color
	// doesn't flip the instant real data lands, on top of the text changing.
	const d = (value) => (loaded ? value : '—');

	return (
		<div className='tw:grid tw:grid-cols-4 tw:max-[1100px]:grid-cols-2 tw:gap-4 tw:mb-6'>
			{dashboardModules.includes('accounts') && (
				<Tile
					catColor={1}
					icon={<FaUser />}
					label={t('entity.account.plural')}
					value={d(accounts.length)}
					sub={`${d(newAccountsCurrentMonth)} nuevas este mes`}
					subTone={loaded && newAccountsCurrentMonth > 0 ? 'up' : 'neutral'}
					onClick={() => navigate('/AdminScreen/accounts')}
				/>
			)}
			{dashboardModules.includes('contracts') && (
				<Tile
					catColor={2}
					icon={<FaHome />}
					label={t('entity.contract.plural')}
					value={d(contracts.length)}
					sub={`${d(newContractsCurrentMonth)} nuevos · ${d(rentalOverdue.count)} cuotas vencidas / ${d(rentalPending.count)} pendientes`}
					subTone={loaded && rentalOverdue.count > 0 ? 'down' : 'neutral'}
					onClick={() => navigate('/AdminScreen/contracts')}
				/>
			)}
			{dashboardModules.includes('assets') && (
				<Tile
					catColor={3}
					icon={<FaChartLine />}
					label={`${t('entity.assetSale.plural')} del mes`}
					value={d(assetSales.length)}
					sub={loaded
						? `${newAssetSalesCurrentMonth} nuevas · ${formatCurrency(currentMonthSalesUSD, 'USD')} / ${formatCurrency(currentMonthSalesUYU, 'UYU')}`
						: '—'}
					subTone={loaded && newAssetSalesCurrentMonth > 0 ? 'up' : 'neutral'}
					onClick={() => navigate('/AdminScreen/assetSales')}
				/>
			)}
			{dashboardModules.includes('assets') && (
				<Tile
					catColor={4}
					icon={<FaBriefcase />}
					label={t('entity.asset.plural')}
					value={d(assets.length)}
					sub={`${d(newAssetsCurrentMonth)} nuevos este mes`}
					subTone={loaded && newAssetsCurrentMonth > 0 ? 'up' : 'neutral'}
					onClick={() => navigate('/AdminScreen/assets')}
				/>
			)}
			{(dashboardModules.includes('debts') || dashboardModules.includes('credits')) && (
				<Tile
					catColor={1}
					icon={<FaMoneyBillWave />}
					label={`${t('entity.credit.plural')} y ${t('entity.debt.plural')}`}
					value={d(credits.length + debts.length)}
					sub={`${d(accountsWithDebts)} con ${t('entity.debt.plural')} / ${d(accountsWithCredits)} con ${t('entity.credit.plural')}`}
					subTone='neutral'
					onClick={() => navigate('/AdminScreen/debts')}
				/>
			)}
			{dashboardModules.includes('contracts') && (
				<Tile
					catColor={2}
					icon={<FaFileContract />}
					label={`${t('entity.contract.plural')}: Activos/Cerrados`}
					value={d(activeContracts)}
					sub={`${d(closedContracts)} cerrados`}
					subTone='neutral'
					onClick={() => navigate('/AdminScreen/contracts')}
				/>
			)}
			{dashboardModules.includes('assets') && (
				<Tile
					catColor={3}
					icon={<FaHistory />}
					label={`${t('entity.assetSale.plural')} históricas`}
					value={loaded ? `${formatCurrency(totalSalesUSD, 'USD')} / ${formatCurrency(totalSalesUYU, 'UYU')}` : '—'}
					sub='Total histórico'
					subTone='neutral'
					onClick={() => navigate('/AdminScreen/assetSales')}
				/>
			)}
			{dashboardModules.includes('assets') && (
				<Tile
					catColor={4}
					icon={<FaBoxOpen />}
					label={`${t('entity.asset.plural')} — Estado`}
					value={d(soldAssets)}
					sub={`${d(availableAssets)} disponibles`}
					subTone='neutral'
					onClick={() => navigate('/AdminScreen/assets')}
				/>
			)}
		</div>
	);
}

export default KpiGrid;
